import { useState, useCallback } from 'react';
import {
  getUserVersions as getUserVersionsFromStorage,
  loadVersionedData as loadVersionedDataFromStorage,
  saveVersionedData as _saveVersionedDataFromStorage
} from '../utils/storage';
import versionService from '../services/versionService';
import { logger } from '../utils/logger';
import { prepareDataWithIds } from '../utils/versionCompare';

/**
 * Custom hook for managing version history functionality
 * @param {string} type - The analyzer type (cv, job, company)
 * @param {string} userId - The user ID
 * @param {object} currentUser - Current user object
 * @returns {object} Version management functions and state
 */
export const useVersionManagement = (type, userId, currentUser) => {
  const [versions, setVersions] = useState([]);
  const [viewingVersion, setViewingVersion] = useState(null);
  const [loadingVersions, setLoadingVersions] = useState(false);

  /**
   * Get component name based on type
   */
  const getComponentName = useCallback((analyzerType) => {
    const componentMap = {
      'cv': 'cv_analyzer',
      'job': 'job_analyzer',
      'company': 'company_research'
    };
    return componentMap[analyzerType] || null;
  }, []);

  /**
   * Determine target user ID based on type
   */
  const getTargetUserId = useCallback(() => {
    return type === 'company' ? 'admin' : (userId || currentUser?.id);
  }, [type, userId, currentUser?.id]);

  /**
   * Load versions for the current analyzer
   */
  const loadVersions = useCallback(async () => {
    try {
      setLoadingVersions(true);
      logger.info('[useVersionManagement]', `Loading versions for type: ${type}`);

      const targetUserId = getTargetUserId();
      if (!targetUserId) {
        logger.warn('[useVersionManagement]', 'No user ID available for loading versions');
        return [];
      }

      const component = getComponentName(type);
      if (!component) {
        logger.warn('[useVersionManagement]', `Unknown component type: ${type}`);
        return [];
      }

      logger.info('[useVersionManagement]', `Loading versions for user ${targetUserId}, component ${component}`);

      // Load version logs using the storage API
      let componentVersions = await getUserVersionsFromStorage(type, targetUserId);
      logger.info('[useVersionManagement]', 'Versions from component directory:', componentVersions);

      // Process the versions if we have any
      if (componentVersions && componentVersions.length > 0) {
        // Format versions to match expected format
        componentVersions = componentVersions.map((version, index) => {
          // Extract version number from timestamp format for display
          let versionNumber = index + 1;
          if (version.version && typeof version.version === 'string' && version.version.includes('_')) {
            const dateParts = version.version.split('_')[0].split('-');
            const timeParts = version.version.split('_')[1].split('-');
            versionNumber = `${dateParts[0]}-${dateParts[1]}-${dateParts[2]} ${timeParts[0]}:${timeParts[1]}`;
          }

          // Determine proper author based on available data
          // For existing versions, preserve the original author
          // Only override if no author is set
          let author = version.author;
          if (!author) {
            // If this is the very first version (oldest), set as System
            // Otherwise use current user email format to match backend
            if (index === componentVersions.length - 1) {
              author = 'System';
            } else if (currentUser) {
              // Use email format to be consistent with backend
              if (currentUser.role === 'admin') {
                author = 'admin@example.com';
              } else if (currentUser.role === 'manager') {
                author = 'manager@example.com';
              } else {
                author = 'user@example.com';
              }
            } else {
              author = 'System';
            }
          }

          return {
            ...version,
            versionNumber: versionNumber,
            author: author,
            isActive: version.isActive || (index === 0),
            timestamp: version.timestamp || version.version,
            // Preserve original version for file operations
            originalVersion: version.version
          };
        });

        // Load actual data for each version
        for (let i = 0; i < componentVersions.length; i++) {
          const version = componentVersions[i];
          const versionStr = version.originalVersion || version.version || '';

          if (versionStr) {
            const filename = `result_v${versionStr}.json`;
            try {
              const versionResponse = await loadVersionedDataFromStorage(targetUserId, component, filename);
              // Extract the actual data from the API response wrapper
              const versionData = versionResponse?.result || versionResponse;

              if (versionData && typeof versionData === 'object') {
                // Debug: Log the actual version data structure
                logger.info('[useVersionManagement]', `Version data loaded for ${versionStr}`, {
                  hasCompetencies: !!versionData.competencies,
                  hasSkills: !!versionData.skills,
                  hasSections: !!versionData.sections,
                  competenciesLength: versionData.competencies?.length || 0,
                  skillsLength: versionData.skills?.length || 0,
                  sectionsLength: versionData.sections?.length || 0,
                  dataKeys: Object.keys(versionData),
                  firstCompetency: versionData.competencies?.[0]?.competency || 'N/A'
                });

                // Store data in the structure expected by useVersionComparison
                componentVersions[i].data = {
                  result: versionData
                };
              } else {
                logger.warn('[useVersionManagement]', `No valid data found for version ${versionStr}`);
              }
            } catch (error) {
              logger.error('[useVersionManagement]', `Error loading data for version ${versionStr}:`, error);
            }
          }
        }
      }

      logger.info('[useVersionManagement]', 'Component versions:', componentVersions);
      setVersions(componentVersions);
      return componentVersions;
    } catch (error) {
      logger.error('[useVersionManagement]', 'Error loading versions:', error);
      return [];
    } finally {
      setLoadingVersions(false);
    }
  }, [type, getTargetUserId, getComponentName, currentUser]);

  /**
   * Load specific version data
   */
  const loadVersionData = useCallback(async (version) => {
    try {
      logger.info('[useVersionManagement]', 'Loading version data:', version);

      const targetUserId = getTargetUserId();
      const component = getComponentName(type);

      if (!component || !targetUserId) {
        logger.error('[useVersionManagement]', 'Missing component or userId');
        return null;
      }

      const versionStr = version.originalVersion || version.version || '';
      const filename = `result_v${versionStr}.json`;

      logger.info('[useVersionManagement]', `Loading version file: ${component}/${filename}`);
      const versionResponse = await loadVersionedDataFromStorage(targetUserId, component, filename);

      // Extract the actual data from the API response wrapper
      const versionData = versionResponse?.result || versionResponse;

      return versionData;
    } catch (error) {
      logger.error('[useVersionManagement]', 'Error loading version data:', error);
      return null;
    }
  }, [type, getTargetUserId, getComponentName]);

  /**
   * Save version data
   */
  const saveVersion = useCallback(async (data, author = null, metadata = {}) => {
    try {
      logger.info('[useVersionManagement]', `Saving version for type: ${type}`);

      const targetUserId = getTargetUserId();
      if (!targetUserId) {
        logger.error('[useVersionManagement]', 'No userId available for saving version');
        return false;
      }

      // Determine proper author based on current user context
      let finalAuthor = author;
      if (!finalAuthor && currentUser) {
        // Use email format to be consistent with backend
        if (currentUser.role === 'admin') {
          finalAuthor = 'admin@example.com';
        } else if (currentUser.role === 'manager') {
          finalAuthor = 'manager@example.com';
        } else {
          finalAuthor = 'user@example.com';
        }
      } else if (!finalAuthor) {
        finalAuthor = 'System';
      }

      logger.info('[useVersionManagement]', `Saving version with author: ${finalAuthor}`);

      // Prepare data with smart IDs before saving
      const processedData = { ...data };

      // Determine data type key and apply ID preparation
      const typeConfig = {
        cv: 'competencies',
        job: 'skills',
        company: 'sections'
      };

      const dataKey = typeConfig[type];
      if (dataKey && processedData[dataKey]) {
        logger.info('[useVersionManagement]', `Preparing ${dataKey} with smart IDs for type: ${type}`);

        // Load previous version data to maintain ID continuity
        let previousItems = [];
        try {
          const previousVersions = versions.length > 0 ? versions : await loadVersions();
          if (previousVersions && previousVersions.length > 0) {
            const latestVersion = previousVersions[0]; // First item should be latest
            if (latestVersion && latestVersion.data && latestVersion.data.result) {
              previousItems = latestVersion.data.result[dataKey] || [];
              logger.info('[useVersionManagement]', `Found ${previousItems.length} previous items for ID reference`);
            }
          }
        } catch (error) {
          logger.warn('[useVersionManagement]', 'Could not load previous items for ID reference:', error);
        }

        // Apply smart ID preparation
        processedData[dataKey] = prepareDataWithIds(processedData[dataKey], previousItems, type);
        logger.info('[useVersionManagement]', `Prepared ${processedData[dataKey].length} items with IDs`, {
          sampleIds: processedData[dataKey].slice(0, 3).map(item => ({ id: item.id, [dataKey === 'competencies' ? 'competency' : dataKey === 'skills' ? 'name' : 'name']: item[dataKey === 'competencies' ? 'competency' : dataKey === 'skills' ? 'name' : 'name'] }))
        });
      }

      // Create version metadata
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const versionMetadata = {
        version: timestamp,
        author: finalAuthor,
        timestamp: new Date().toISOString(),
        ...metadata
      };

      // Save to storage with version (let backend handle versioning automatically)
      const component = getComponentName(type);
      const filename = 'result.json';  // Use standard filename, backend creates versions

      // Save processed data with operation metadata using versionService directly
      const result = await versionService.saveVersionedData(targetUserId, component, filename, {
        ...processedData,
        metadata: versionMetadata
      }, finalAuthor, {
        operation: metadata.operation,
        description: metadata.description
      });

      logger.info('[useVersionManagement]', 'Version saved with smart IDs:', result);

      // Reload versions to update the list
      await loadVersions();

      return result;
    } catch (error) {
      logger.error('[useVersionManagement]', 'Error saving version:', error);
      return false;
    }
  }, [type, getTargetUserId, getComponentName, loadVersions, versions, currentUser]);

  /**
   * Restore a specific version by creating a new version (no data loss)
   */
  const restoreVersion = useCallback(async (version) => {
    try {
      logger.info('[useVersionManagement]', 'Restoring version as new version:', version);

      const targetUserId = getTargetUserId();
      const component = getComponentName(type);

      if (!component || !targetUserId) {
        logger.error('[useVersionManagement]', 'Missing component or userId for restore');
        return null;
      }

      const versionStr = version.originalVersion || version.version || '';
      const versionFilename = `result_v${versionStr}.json`;
      const targetFilename = 'result.json';

      logger.info('[useVersionManagement]', `Restoring version ${versionFilename} as new version for: ${component}`);

      // Use the new restore as new functionality
      const result = await versionService.restoreVersionAsNew(
        targetUserId,
        component,
        targetFilename,
        versionFilename
      );

      if (!result || !result.result) {
        logger.error('[useVersionManagement]', 'Failed to restore version as new, result:', result);
        return null;
      }

      logger.info('[useVersionManagement]', 'Version restored as new:', result.result);

      // Wait a moment for backend to finish writing files
      await new Promise(resolve => setTimeout(resolve, 500));

      // Load the restored data - try multiple times if needed
      let restoredData = null;
      let attempts = 0;
      const maxAttempts = 3;

      while (!restoredData && attempts < maxAttempts) {
        attempts++;
        try {
          const restoredResponse = await loadVersionedDataFromStorage(targetUserId, component, targetFilename);
          restoredData = restoredResponse?.result || restoredResponse;
          if (restoredData && Object.keys(restoredData).length > 0) {
            logger.info('[useVersionManagement]', `Successfully loaded restored data on attempt ${attempts}`);
            break;
          } else {
            logger.warn('[useVersionManagement]', `Attempt ${attempts}: Empty or null restored data, retrying...`);
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        } catch (error) {
          logger.error('[useVersionManagement]', `Attempt ${attempts} failed:`, error);
          if (attempts === maxAttempts) throw error;
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      if (!restoredData || Object.keys(restoredData).length === 0) {
        logger.error('[useVersionManagement]', 'No data found after restore operation');
        return null;
      }

      // Prepare restored data with smart IDs to maintain consistency
      const typeConfig = {
        cv: 'competencies',
        job: 'skills',
        company: 'sections'
      };

      const dataKey = typeConfig[type];
      if (dataKey && restoredData[dataKey]) {
        logger.info('[useVersionManagement]', `Preparing restored ${dataKey} with smart IDs for type: ${type}`);

        // Load current data to maintain ID continuity (if any exists)
        let currentItems = [];
        try {
          const currentVersions = await loadVersions();
          if (currentVersions && currentVersions.length > 0) {
            const latestVersion = currentVersions[0]; // First item should be latest
            if (latestVersion && latestVersion.data && latestVersion.data.result) {
              currentItems = latestVersion.data.result[dataKey] || [];
              logger.info('[useVersionManagement]', `Found ${currentItems.length} current items for ID reference`);
            }
          }
        } catch (error) {
          logger.warn('[useVersionManagement]', 'Could not load current items for ID reference:', error);
        }

        // Apply smart ID preparation to restored data
        restoredData[dataKey] = prepareDataWithIds(restoredData[dataKey], currentItems, type);
        logger.info('[useVersionManagement]', `Prepared ${restoredData[dataKey].length} restored items with IDs`);
      }

      // Reload versions to show the new version created from restore
      await loadVersions();

      return restoredData;
    } catch (error) {
      logger.error('[useVersionManagement]', 'Error restoring version as new:', error);
      throw error; // Re-throw to allow UI to handle the error
    }
  }, [type, getTargetUserId, getComponentName, loadVersions]);

  return {
    versions,
    setVersions,
    viewingVersion,
    setViewingVersion,
    loadingVersions,
    loadVersions,
    loadVersionData,
    saveVersion,
    restoreVersion,
    getTargetUserId,
    getComponentName
  };
};
